import type {
  EditConfirmRequest,
  EditData,
  GenerationProgress,
  GenerationResults,
  GenerationStartRequest,
  GenerationStartResponse,
  Keyword,
  KeywordCreate,
  KeywordListParams,
  KeywordListResponse,
  KeywordListWithSummary,
  KeywordListWithSummaryParams,
  KeywordRankHistoryResponse,
  KeywordUpdate,
  MessageResponse,
  PlaceKeywordSettings,
  PlaceKeywordSettingsUpdate,
  SaveKeywordsRequest,
  SaveKeywordsResponse,
} from '../types';
import apiClient from './apiClient';

export const keywordService = {
  async listKeywords(
    placeId: number,
    params: KeywordListParams = {}
  ): Promise<KeywordListResponse> {
    const response = await apiClient.get<KeywordListResponse>(
      `/keywords/places/${placeId}/keywords`,
      { params }
    );
    return response.data;
  },

  async getKeyword(keywordId: number): Promise<Keyword> {
    const response = await apiClient.get<Keyword>(`/keywords/${keywordId}`);
    return response.data;
  },

  async createKeyword(placeId: number, data: KeywordCreate): Promise<Keyword> {
    const response = await apiClient.post<Keyword>(
      `/keywords/places/${placeId}/keywords`,
      data
    );
    return response.data;
  },

  async updateKeyword(keywordId: number, data: KeywordUpdate): Promise<Keyword> {
    const response = await apiClient.patch<Keyword>(`/keywords/${keywordId}`, data);
    return response.data;
  },

  async deleteKeyword(keywordId: number): Promise<MessageResponse> {
    const response = await apiClient.delete<MessageResponse>(`/keywords/${keywordId}`);
    return response.data;
  },

  async getKeywordRankHistory(
    keywordId: number,
    params?: { start_date?: string; end_date?: string; period?: '7d' | '30d' | '90d' }
  ): Promise<KeywordRankHistoryResponse> {
    const response = await apiClient.get<KeywordRankHistoryResponse>(
      `/keywords/${keywordId}/rank-history`,
      { params }
    );
    return response.data;
  },

  async getKeywordSettings(placeId: number): Promise<PlaceKeywordSettings> {
    const response = await apiClient.get<PlaceKeywordSettings>(
      `/places/${placeId}/keyword-settings`
    );
    return response.data;
  },

  async updateKeywordSettings(
    placeId: number,
    data: PlaceKeywordSettingsUpdate
  ): Promise<PlaceKeywordSettings> {
    const response = await apiClient.patch<PlaceKeywordSettings>(
      `/places/${placeId}/keyword-settings`,
      data
    );
    return response.data;
  },

  async startGeneration(
    placeId: number,
    data: GenerationStartRequest
  ): Promise<GenerationStartResponse> {
    const response = await apiClient.post<GenerationStartResponse>(
      `/places/${placeId}/keywords/generate`,
      data
    );
    return response.data;
  },

  async getActiveGeneration(placeId: number): Promise<GenerationProgress | null> {
    const response = await apiClient.get<GenerationProgress | null>(
      `/places/${placeId}/keywords/generate/active`
    );
    return response.data;
  },

  async getGenerationProgress(
    placeId: number,
    runId: number
  ): Promise<GenerationProgress> {
    const response = await apiClient.get<GenerationProgress>(
      `/places/${placeId}/keywords/generate/${runId}`
    );
    return response.data;
  },

  async getGenerationResults(
    placeId: number,
    runId: number,
    params?: { sort?: string; order?: string; map_type?: string }
  ): Promise<GenerationResults> {
    const response = await apiClient.get<GenerationResults>(
      `/places/${placeId}/keywords/generate/${runId}/results`,
      { params }
    );
    return response.data;
  },

  async saveGeneratedKeywords(
    placeId: number,
    runId: number,
    data: SaveKeywordsRequest
  ): Promise<SaveKeywordsResponse> {
    const response = await apiClient.post<SaveKeywordsResponse>(
      `/places/${placeId}/keywords/generate/${runId}/save`,
      data
    );
    return response.data;
  },

  async listKeywordsWithSummary(
    placeId: number,
    params: KeywordListWithSummaryParams = {}
  ): Promise<KeywordListWithSummary> {
    const response = await apiClient.get<KeywordListWithSummary>(
      `/places/${placeId}/keywords-with-summary`,
      { params }
    );
    return response.data;
  },

  async exportKeywords(
    placeId: number,
    format: 'csv' | 'xlsx' = 'csv',
    includeUrl: boolean = false
  ): Promise<Blob> {
    const response = await apiClient.get(
      `/places/${placeId}/keywords/export`,
      {
        params: { format, include_url: includeUrl },
        responseType: 'blob',
      }
    );
    return response.data;
  },

  async copyAllKeywords(placeId: number): Promise<string> {
    const response = await apiClient.get<MessageResponse>(
      `/places/${placeId}/keywords/copy`
    );
    return response.data.message;
  },

  async getEditData(placeId: number, runId: number): Promise<EditData> {
    const response = await apiClient.get<EditData>(
      `/places/${placeId}/keywords/generate/${runId}/edit-data`
    );
    return response.data;
  },

  async confirmEditData(
    placeId: number,
    runId: number,
    data: EditConfirmRequest
  ): Promise<void> {
    await apiClient.post(
      `/places/${placeId}/keywords/generate/${runId}/confirm`,
      data
    );
  },

  async applyPartialResults(
    placeId: number,
    runId: number
  ): Promise<{ success: boolean; applied_count: number }> {
    const response = await apiClient.post<{ success: boolean; applied_count: number }>(
      `/places/${placeId}/keywords/generate/${runId}/apply`
    );
    return response.data;
  },

  async rollbackPartialResults(
    placeId: number,
    runId: number
  ): Promise<{ success: boolean; deleted_count: number }> {
    const response = await apiClient.post<{ success: boolean; deleted_count: number }>(
      `/places/${placeId}/keywords/generate/${runId}/rollback`
    );
    return response.data;
  },

  async cancelActiveGeneration(placeId: number): Promise<void> {
    await apiClient.delete(`/places/${placeId}/keywords/generate/active`);
  },
};
